import React from 'react';
import { Box, chakra } from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import { TRANSITION_BORDER, TRANSITION_CHEVRON } from '@/components/ui/motion.ts';

interface DropdownTriggerProps {
	isOpen: boolean;
	onToggle: () => void;
	children: React.ReactNode;
	ariaLabel?: string;
}

/**
 * Field-styled dropdown trigger: at rest it reads as an input (pill border, white surface), and on
 * hover it darkens its border like a field rather than filling its background like a button.
 */
const DropdownTrigger: React.FC<DropdownTriggerProps> = ({ isOpen, onToggle, children, ariaLabel }) => {
	return (
		<chakra.button
			type="button"
			aria-haspopup="listbox"
			aria-expanded={isOpen}
			aria-label={ariaLabel}
			onClick={onToggle}
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			gap={3}
			minH="42px"
			px={3}
			borderRadius="full"
			border="1px solid"
			borderColor="border.default"
			bg="surface.default"
			boxShadow="none"
			cursor="pointer"
			textStyle="body.sm"
			fontWeight={500}
			textAlign="left"
			whiteSpace="nowrap"
			transition={TRANSITION_BORDER}
			_hover={{ borderColor: 'border.strong' }}
			_focusVisible={{ outline: '2px solid', outlineColor: 'brand.700', outlineOffset: '2px' }}
		>
			{children}
			<Box
				as={FiChevronDown}
				boxSize="10px"
				transition={TRANSITION_CHEVRON}
				transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
				flexShrink={0}
			/>
		</chakra.button>
	);
};

export default DropdownTrigger;
